import { t } from 'i18next'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { Column } from 'primereact/column'
import { DataTable } from 'primereact/datatable'
import { Toast } from 'primereact/toast'
import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { GroupForm } from './GroupForm'
import { claims } from '../../../../claims'
import { API_GROUP_BY_ID, API_GROUPS } from '../../../../constants'
import { RequestMethod } from '../../../../model/enums/enums'
import { Group } from '../../../../model/group.model'
import {
  addGroup,
  removeGroup,
} from '../../../../redux/basic/group/groupActions'
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks'
import { AuthRoutes } from '../../../../router/routes'
import { handleRequest } from '../../../../services/api/base/handleRequest'
import { hasRequiredClaim } from '../../../../util/validation/hasClaim'
import { BackButton } from '../../../util/button/BackButton'
import { actionBody } from '../../../util/data-table/cell-body/ActionBody'
import { dateBody } from '../../../util/data-table/cell-body/BirthdayBody'
import { DeleteConfirmationDialog } from '../../../util/dialog/DeleteConfirmationDialog'

// default group
// it is important that group id is not -1
const defaultGroup: Group = {
  group_name: '',
  group_description: '',
  is_active: 1,
  group_id: 0,
  last_change: undefined,
}
export const GroupModule = (): JSX.Element => {
  const userClaims = useAppSelector((state) => state.claims)
  const groups = useAppSelector((state) => state.groups)
  const dispatcher = useAppDispatch()

  const navigate = useNavigate()

  const toast = useRef<Toast>(null)

  const [showDeleteWithMemberDialog, setShowDeleteWithMemberDialog] =
    useState<boolean>(false)

  const [selectedGroup, setSelectedGroup] = useState<Group>(defaultGroup)

  // navigate to the group page that we want to edit
  const edit = (rowData: Group): void => {
    navigate(`${AuthRoutes.groups}/${rowData.group_id}`)
  }

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false)

  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onSubmit',
    shouldFocusError: true,
    defaultValues: defaultGroup,
  })

  // subscribe to activate button on changes
  useEffect(() => {
    const subscription = methods.watch(async (value, { name, type }) => {
      if (type === 'change' || type === undefined) {
        setHasUnsavedChanges(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [])

  const onClickGroupRemove = (rowData: Group) => {
    setSelectedGroup(rowData)
    setShowDeleteWithMemberDialog(true)
  }

  const deleteSelectedGroup = async () => {
    const result = await handleRequest(
      API_GROUP_BY_ID(selectedGroup.group_id),
      RequestMethod.DELETE
    )
    if (result.error) {
      console.warn(
        '[Group Module] Error occurred on the backend, trying to delete the Group.'
      )
      // todo show error
      return
    }

    dispatcher(removeGroup(selectedGroup.group_id))
    setSelectedGroup(defaultGroup)
    setShowDeleteWithMemberDialog(false)
  }

  const deleteButton = (rowData: Group) => {
    const disabled =
      rowData.group_apprenticeship_module_plans &&
      rowData.group_apprenticeship_module_plans.length > 0
    let tooltip = disabled
      ? t<string>('hrm.group.message.delete.dependency')
      : t<string>('core.delete')
    return (
      <Button
        type={'button'}
        icon="fa-solid fa-trash"
        className="p-button-danger p-button-text ml-auto"
        tooltip={tooltip}
        tooltipOptions={{
          style: { minWidth: '100px' },
          showDelay: 500,
          showOnDisabled: true,
          position: 'left',
        }}
        disabled={
          rowData.group_apprenticeship_module_plans &&
          rowData.group_apprenticeship_module_plans.length > 0
        }
        onClick={() => onClickGroupRemove(rowData)}
      />
    )
  }

  const descriptionBody = (value: string, maxLength: number) => {
    if (value && value.length > maxLength) {
      return value.substring(0, maxLength) + '...'
    }
    return value
  }

  const onSubmit = async (formData: Group) => {
    const result = await handleRequest<Group>(
      API_GROUPS(),
      RequestMethod.POST,
      formData
    )

    if (result.error || !result.data) {
      console.warn('[Group Module] Adding a new group failed')
      return
    }

    dispatcher(addGroup(result.data))
    // we reset the form so that one can quickly add another group
    methods.reset()
    setHasUnsavedChanges(false)

    toast.current?.show({
      closable: true,
      severity: 'success',
      summary: t<string>('core.action.add.confirmed.title'),
      detail: t<string>('hrm.group.message.add.success'),
      life: 12000,
    })
    return
  }

  return (
    <div className={'pr-2'}>
      <BackButton
        translationKey={'navigation.go_human_resources'}
        route={AuthRoutes.hrm}
      />

      {
        // we only show the add form if the user can create groups
        hasRequiredClaim(userClaims, claims.hrm.groups.create) && (
          <Card>
            <h3>{t<string>('hrm.group.add')}</h3>

            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div
                  className={
                    'formgrid grid flex-wrap p-fluid justify-content-start align-items-center'
                  }
                >
                  <div className={'flex flex-wrap col md:col-10 '}>
                    <GroupForm
                      disabled={
                        !hasRequiredClaim(userClaims, claims.hrm.groups.create)
                      }
                    />
                  </div>
                  <div
                    className={
                      'col-12 md:col-2 flex justify-content-center pl-2'
                    }
                  >
                    <Button
                      type={'submit'}
                      label={t<string>('hrm.group.add')}
                      disabled={!hasUnsavedChanges}
                    />
                  </div>
                </div>
              </form>
            </FormProvider>
          </Card>
        )
      }
      <div style={{ height: 'calc(100vh - 360px)', overflow: 'auto' }}>
        <DataTable
          value={groups}
          header={t<string>('hrm.group.all')}
          scrollHeight={'flex'}
          scrollable
          className={'p-datatable'}
          rowHover
          stripedRows
          selectionMode="single"
          responsiveLayout="stack"
          emptyMessage={t<string>('core.empty_message')}
        >
          <Column
            header={t<string>('hrm.group.group_name')}
            field={'group_name'}
            sortable={true}
            filterPlaceholder={t<string>('core.search')}
            filterField={'group_name'}
          />
          <Column
            header={t<string>('hrm.group.description')}
            field={'group_description'}
            showFilterMenuOptions={true}
            filterPlaceholder={t<string>('core.search')}
            filterField={'group_description'}
            body={(group) => descriptionBody(group.group_description, 90)}
          />
          <Column
            header={t<string>('hrm.group.members_count')}
            field={'group_member'}
            body={(e: Group) => e.group_member?.length || 0}
            sortable={true}
          />
          <Column
            header={t<string>('core.last_change')}
            field={'last_change'}
            bodyClassName={'secondary-text small-text'}
            sortable={true}
            body={(e) => dateBody(e.last_change)}
          />

          <Column body={actionBody(edit)} style={{ maxWidth: '44px' }} />

          {
            // we only show the delete button if the user can delete
            hasRequiredClaim(userClaims, claims.hrm.groups.delete) && (
              <Column body={deleteButton} style={{ maxWidth: '44px' }} />
            )
          }
        </DataTable>
      </div>

      <DeleteConfirmationDialog
        message={t<string>('hrm.group.message.delete.message', {
          groupMemberCount: selectedGroup.group_member?.length || 0,
        })}
        title={selectedGroup?.group_name}
        dataToDelete={selectedGroup}
        visible={showDeleteWithMemberDialog}
        onClose={() => setShowDeleteWithMemberDialog(false)}
        onDelete={deleteSelectedGroup}
      />

      <Toast className={' pt-2 mt-2'} ref={toast} position="bottom-right" />
    </div>
  )
}